import { useEffect, useMemo, useState } from "react";
import DOMPurify from "dompurify";
import { fetchShopPolicies, type ShopPolicy } from "@/lib/shopify";

type PolicyKey = "shippingPolicy" | "refundPolicy" | "termsOfService" | "privacyPolicy";

interface PolicyPageProps {
  policyKey: PolicyKey;
  fallbackTitle: string;
}

export function PolicyPage({ policyKey, fallbackTitle }: PolicyPageProps) {
  const [policy, setPolicy] = useState<ShopPolicy | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchShopPolicies()
      .then((policies) => {
        if (cancelled) return;
        setPolicy(policies?.[policyKey] ?? null);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setError(true);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [policyKey]);

  const title = policy?.title || fallbackTitle;
  const sanitizedBody = useMemo(() => {
    if (!policy?.body) return "";
    let html = DOMPurify.sanitize(policy.body);
    if (policyKey === "privacyPolicy") {
      // Shopify's default privacy policy ships an empty <a>here</a> in the
      // "Right to Opt out" section. Point it at our opt-out page.
      html = html.replace(
        /<a(?![^>]*\bhref\s*=\s*["'][^"']+["'])[^>]*>\s*here\s*<\/a>/gi,
        '<a href="/policies/your-privacy-choices">here</a>'
      );
    }
    return html;
  }, [policy?.body, policyKey]);

  return (
    <div className="bg-background">
      {/* Hero */}
      <section className="bg-[#FDFAF3]">
        <div className="mx-auto max-w-[1400px] px-6 pt-8 pb-16 text-center">
          <h1 className="font-heading text-5xl uppercase tracking-wide text-primary md:text-6xl">
            {title}
          </h1>
        </div>
      </section>

      {/* Body */}
      <section className="mx-auto max-w-3xl px-6 py-16">
        {loading && (
          <div className="space-y-4">
            <div className="h-4 w-3/4 animate-pulse rounded bg-muted" />
            <div className="h-4 w-full animate-pulse rounded bg-muted" />
            <div className="h-4 w-5/6 animate-pulse rounded bg-muted" />
            <div className="h-4 w-full animate-pulse rounded bg-muted" />
            <div className="h-4 w-2/3 animate-pulse rounded bg-muted" />
          </div>
        )}

        {!loading && error && (
          <p className="font-body text-base text-foreground/70">
            We're having trouble loading this policy right now. Please try again later or
            contact us directly.
          </p>
        )}

        {!loading && !error && !policy && (
          <p className="font-body text-base text-foreground/70">
            This policy hasn't been published yet. Please check back soon or reach out to
            our team for details.
          </p>
        )}

        {!loading && !error && policy && (
          <article
            className="policy-prose font-body text-base leading-relaxed text-foreground"
            dangerouslySetInnerHTML={{ __html: sanitizedBody }}
          />
        )}
      </section>
    </div>
  );
}
